/**
 * Normaliza os contratos de Runtime Intelligence (Etapa 20):
 * `modules.behavior.rateLimit`, `.buffer`, `.stateMachine`, `.sessionSummary`.
 * Determinístico e defensivo — sem fallback legacy, sem aliases.
 */

import type { BehaviorModuleConfig } from '@/config/types/modules/behavior';

import type {
  BufferDropStrategy,
  NormalizedBufferConfig,
  NormalizedRateLimitConfig,
  NormalizedSessionSummaryConfig,
  NormalizedStateMachineConfig,
} from './contracts';

export const RATE_LIMIT_MIN_WINDOW_MS = 100;
export const BUFFER_MIN_MAX_SIZE = 1;
export const STATE_MACHINE_MIN_HISTORY = 1;
export const STATE_MACHINE_MAX_HISTORY = 1_000;

const VALID_DROP_STRATEGIES: readonly BufferDropStrategy[] = [
  'drop_oldest',
  'drop_newest',
  'compress_similar',
];

function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function normalizeRateLimitConfig(
  raw: Partial<NormalizedRateLimitConfig> | undefined
): NormalizedRateLimitConfig {
  let windowMs = Math.floor(toFiniteNumber(raw?.windowMs, 1_000));
  if (windowMs < RATE_LIMIT_MIN_WINDOW_MS) windowMs = RATE_LIMIT_MIN_WINDOW_MS;

  let maxSignalsPerWindow = Math.floor(
    toFiniteNumber(raw?.maxSignalsPerWindow, 50)
  );
  if (maxSignalsPerWindow < 1) maxSignalsPerWindow = 1;

  return {
    enabled: raw?.enabled === true,
    windowMs,
    maxSignalsPerWindow,
    criticalBypass: toBoolean(raw?.criticalBypass, true),
  };
}

export function normalizeBufferConfig(
  raw: Partial<NormalizedBufferConfig> | undefined
): NormalizedBufferConfig {
  let maxSize = Math.floor(toFiniteNumber(raw?.maxSize, 200));
  if (maxSize < BUFFER_MIN_MAX_SIZE) maxSize = BUFFER_MIN_MAX_SIZE;

  let maxAgeMs = Math.floor(toFiniteNumber(raw?.maxAgeMs, 30_000));
  if (maxAgeMs < 0) maxAgeMs = 0;

  let flushSize = Math.floor(toFiniteNumber(raw?.flushSize, 50));
  if (flushSize < 1) flushSize = 1;
  if (flushSize > maxSize) flushSize = maxSize;

  const dropStrategy: BufferDropStrategy =
    raw?.dropStrategy && VALID_DROP_STRATEGIES.includes(raw.dropStrategy)
      ? raw.dropStrategy
      : 'compress_similar';

  return {
    enabled: raw?.enabled === true,
    maxSize,
    maxAgeMs,
    flushSize,
    dropStrategy,
    flushOnHidden: toBoolean(raw?.flushOnHidden, true),
  };
}

export function normalizeStateMachineConfig(
  raw: Partial<NormalizedStateMachineConfig> | undefined
): NormalizedStateMachineConfig {
  let historyLimit = Math.floor(toFiniteNumber(raw?.historyLimit, 50));
  if (historyLimit < STATE_MACHINE_MIN_HISTORY) {
    historyLimit = STATE_MACHINE_MIN_HISTORY;
  }
  if (historyLimit > STATE_MACHINE_MAX_HISTORY) {
    historyLimit = STATE_MACHINE_MAX_HISTORY;
  }

  return {
    enabled: raw?.enabled === true,
    historyLimit,
  };
}

export function normalizeSessionSummaryConfig(
  raw: Partial<NormalizedSessionSummaryConfig> | undefined
): NormalizedSessionSummaryConfig {
  let minimumSessionMs = Math.floor(toFiniteNumber(raw?.minimumSessionMs, 0));
  if (minimumSessionMs < 0) minimumSessionMs = 0;

  return {
    enabled: raw?.enabled === true,
    emitOnHidden: raw?.emitOnHidden === true,
    emitOnDestroy: toBoolean(raw?.emitOnDestroy, true),
    minimumSessionMs,
  };
}

export type NormalizedRuntimeIntelligenceConfig = {
  readonly rateLimit: NormalizedRateLimitConfig;
  readonly buffer: NormalizedBufferConfig;
  readonly stateMachine: NormalizedStateMachineConfig;
  readonly sessionSummary: NormalizedSessionSummaryConfig;
};

export function resolveRuntimeIntelligenceConfig(
  behavior: BehaviorModuleConfig
): NormalizedRuntimeIntelligenceConfig {
  return {
    rateLimit: normalizeRateLimitConfig(behavior.rateLimit),
    buffer: normalizeBufferConfig(behavior.buffer),
    stateMachine: normalizeStateMachineConfig(behavior.stateMachine),
    sessionSummary: normalizeSessionSummaryConfig(behavior.sessionSummary),
  };
}
